import { useCallback, useEffect, useState } from 'react';
import { useCouponsStatistics } from '../../../hooks/useStatistics';
import {
  GERENCIALES_REPORT_OPTIONS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_MESSAGES,
} from '../constants/gerencialesConstants';

/**
 * Obtiene el rango del mes actual en formato YYYY-MM-DD.
 * @returns {{dateStart: string, dateEnd: string}} Rango del mes actual.
 */
const getMonthDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const toDateString = (dateValue) => dateValue.toISOString().split('T')[0];

  return {
    dateStart: toDateString(start),
    dateEnd: toDateString(end),
  };
};

/**
 * Hook para obtener y mantener estadisticas gerenciales de contratos.
 * @returns {Object} Estado y acciones para la vista Gerenciales.
 */
export const useGerencialesStatistics = () => {
  const { mutateAsync: fetchStatistics } = useCouponsStatistics();
  const [reportType, setReportType] = useState(GERENCIALES_REPORT_TYPE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [header, setHeader] = useState(null);
  const [filters, setFilters] = useState(() => getMonthDateRange());

  const requiresDateRange = GERENCIALES_DATE_RANGE_REPORTS.includes(reportType);

  /**
   * Maneja el cambio de filtros de fecha.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del input.
   * @returns {void} Actualiza el filtro.
   */
  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Solicita al backend las estadisticas gerenciales.
   * @returns {Promise<void>} Promesa de carga.
   */
  const loadStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (requiresDateRange) {
        if (!filters.dateStart || !filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.DATES_REQUIRED);
          setResults(null);
          setHeader(null);
          return;
        }
        if (filters.dateStart > filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.INVALID_RANGE);
          setResults(null);
          setHeader(null);
          return;
        }
      }

      const payload = { reportType };
      if (requiresDateRange) {
        payload.dateStart = filters.dateStart;
        payload.dateEnd = filters.dateEnd;
      }

      const response = await fetchStatistics(payload);
      setResults(response?.results || null);
      setHeader(response?.header || null);
    } catch (fetchError) {
      console.error('Error fetching managerial statistics:', fetchError);
      setError('Error al obtener las estadisticas.');
      setResults(null);
      setHeader(null);
    } finally {
      setLoading(false);
    }
  }, [fetchStatistics, reportType, requiresDateRange, filters.dateStart, filters.dateEnd]);

  useEffect(() => {
    if (requiresDateRange && (!filters.dateStart || !filters.dateEnd)) {
      return;
    }
    loadStatistics();
  }, [loadStatistics, requiresDateRange, filters.dateStart, filters.dateEnd]);

  /**
   * Maneja el cambio de tipo de reporte.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza el tipo de reporte.
   */
  const handleReportTypeChange = (event) => {
    const { value } = event.target;
    const isValid = GERENCIALES_REPORT_OPTIONS.some((option) => option.value === value);
    if (!isValid) return;
    setReportType(value);
    if (GERENCIALES_DATE_RANGE_REPORTS.includes(value)) {
      setFilters(getMonthDateRange());
    } else {
      setFilters({ dateStart: '', dateEnd: '' });
    }
    if (error) {
      setError(null);
    }
  };

  return {
    reportType,
    loading,
    error,
    results,
    header,
    filters,
    reload: loadStatistics,
    handleReportTypeChange,
    handleDateChange,
  };
};
